/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { AsyncResult } from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { Collapse, Drawer, Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState, useEffect } from 'react';
import Logo from './components/logo.component';
import { IHeaderProps, IMyAccountLinksInMaoriData } from '../definition-extensions/header.ext.props.autogenerated';
import MsDyn365, * as Msdyn365 from '@msdyn365-commerce/core';
// import { BsFillArrowRightCircleFill, BsChevronDown } from 'react-icons/bs';
import { getAsset } from '@msdyn365-commerce/core';
import { readAsync, updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import { AXE_MAORI_LANG_CODE, HEADER_FOOTER } from '../../../utilities/constants';
import {
    getToggleLang,
    getUserPrefLang,
    getUserPreferredLanguage,
    setOnlineChannelInfoInCache,
    getOnlineChannelInfoTimer,
    IS_LOCAL_DEVELOPMENT
} from '../../../utilities/language-change';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    onTelemetryClick,
    TelemetryConstant,
    getTelemetryObject
} from '@msdyn365-commerce-modules/utilities';
import { getMOEOnlineStoresAsync } from '../../../actions/extensions/DataActionExtension.g';

const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = props => {
    const { HeaderTag, HeaderContainer, HeaderTopBarContainer, resources, context } = props;
    const {
        logoLink,
        logoImage,
        kauwhatareoRedirectURL,
        nzcRedirectURL,
        headerText,
        supportBtnUrl,
        headerPatternImage,
        headerTextLink,
        siteName
    } = props.config;

    // State variables
    // const [switchCurriculumUrl, setSwitchCurriculumUrl] = useState<any>('select');
    // const [switchCurriculumName, setSwitchCurriculumName] = useState<any>('Switch curriculum');
    // const [toggleCurriculumState, setToggleCurriculumState] = useState<any>(false);
    const [preferredLanguage, setPreferredLanguage] = useState<string>('english');
    const hostname = context.request.url?.requestUrl?.hostname;

    useEffect(() => {
        // Identifying Language
        const toggleLang = getToggleLang(context);
        const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
        setPreferredLanguage(getUserPreferredLanguage(siteName as any, HEADER_FOOTER, userPrefLang, toggleLang, context));

        if (IS_LOCAL_DEVELOPMENT) {
            return;
        }

        if (MsDyn365.isBrowser && !localStorage.getItem(`moeOnlineChannelInfo${hostname}`)) {
            void getMOEOnlineStoresAsync({ callerContext: props.context.actionContext, bypassCache: 'get' }).then(channelResponse => {
                setOnlineChannelInfoInCache(context, JSON.stringify(channelResponse));
            });
        } else {
            const hours = 24;
            const setupTime = getOnlineChannelInfoTimer(context);
            const now = new Date().getTime();

            if (MsDyn365.isBrowser && now - setupTime > hours * 60 * 60 * 1000) {
                localStorage.removeItem('moeOnlineChannelInfoTimer');
                void getMOEOnlineStoresAsync({ callerContext: props.context.actionContext, bypassCache: 'get' }).then(channelResponse => {
                    setOnlineChannelInfoInCache(context, JSON.stringify(channelResponse));
                });
            }
        }
    }, []);

    // bug 102348: Effect for changing document lang code to Maori
    React.useEffect(() => {
        const htmlElement: HTMLElement | null = document.querySelector('html');
        if (htmlElement) {
            htmlElement.lang = AXE_MAORI_LANG_CODE;
            htmlElement.translate = false;
        }
    }, []);

    // const changeSwitchCurriculum = (curriculum: any) => {
    //     if (curriculum === 'nzc') {
    //         setSwitchCurriculumUrl(nzcRedirectURL);
    //         setSwitchCurriculumName('NZC');
    //         setToggleCurriculumState(!toggleCurriculumState);
    //     } else if (curriculum === 'kwr') {
    //         setSwitchCurriculumUrl(kauwhatareoRedirectURL);
    //         setSwitchCurriculumName('Kauwhata reo');
    //         setToggleCurriculumState(!toggleCurriculumState);
    //     }
    // };

    // const toggleCurriculum = () => {
    //     setToggleCurriculumState(!toggleCurriculumState);
    // };

    function _renderPatternImage(item: Msdyn365.IImageData | undefined, ariaHiddenAttr?: boolean) {
        if (!item || !item.src || item.src.length === 0) {
            return null;
        }

        return (
            <div className='ms-header-pattern-image'>
                <Msdyn365.Image
                    {...item}
                    requestContext={props.context.request}
                    gridSettings={props.context.request.gridSettings!}
                    imageSettings={item.imageSettings}
                    loadFailureBehavior='default'
                    aria-hidden={ariaHiddenAttr}
                    editProps={{ key: props.config.headerPatternImage || {}, requestContext: props.context.request }}
                    shouldSkipToMainImage
                />
            </div>
        );
    }

    function _renderCollapseMenu(props: IHeaderViewProps & IHeaderProps<{}>): JSX.Element | null {
        const { Divider, MobileMenuLinksContainer, mobileMenuCollapsed } = props;
        return (
            <Collapse className='ms-header__collapsible-hamburger' isOpen={!mobileMenuCollapsed}>
                {_renderReactFragment(props.menuBar)}
                <Node {...Divider} />
                <Node {...MobileMenuLinksContainer}>
                    {_renderReactFragment(props.search)}
                    <div className='moe-site-controls'>
                        <div className='moe-site-controls--left'>
                            {_renderMobileAccountBlock(props, true, preferredLanguage)}
                            <div className='moe-support-btn'>
                                <a className='msc-btn moe-support-btn--link' role='button' href={supportBtnUrl}>
                                    <i className='far fa-question-circle'></i>
                                    <text>
                                        {preferredLanguage === 'maori' ? resources.supportBtnTextInMaori : resources.supportBtnText}
                                    </text>
                                </a>
                            </div>
                        </div>

                        <div className='moe-site-controls--right'>
                            <span className='moe-site-controls__lbl'>Switch Curriculum:</span>
                            <div className='moe-switchsite'>
                                <a className='moe-switchsite__link' href={nzcRedirectURL} target='_blank'>
                                    <img
                                        className='moe-switchsite__img nzc-logo'
                                        src={getAsset('images/nzc-thumbnail.png', props.context.request)}
                                        title='New Zealand Curriculum'
                                        alt='New Zealand Curriculum logo'
                                    />
                                </a>
                                <a className='moe-switchsite__link' href={kauwhatareoRedirectURL} target='_blank'>
                                    <img
                                        className='moe-switchsite__img kwr-logo'
                                        src={getAsset('images/kwr-thumbnail.png', props.context.request)}
                                        title='Kauwhata Reo'
                                        alt='Kauwhata Reo logo'
                                    />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* <div className='moe-form__control moe-form__control--switchcurriculum' aria-label='Switch curriculum form'>
                        <div className='moe-select'>
                            <button onClick={toggleCurriculum} className='moe-select__button'>
                                {switchCurriculumName}
                                <span className='moe-select__chevrondown'>
                                    <BsChevronDown />
                                </span>
                            </button>
                            <a
                                className='moe-form__control-gobtn'
                                href={switchCurriculumUrl}
                                style={{ pointerEvents: switchCurriculumUrl === 'select' ? 'none' : 'auto' }}
                            >
                                <span className='sr-only'>Go</span>
                                <BsFillArrowRightCircleFill />
                            </a>
                        </div>
                        <ul className={`moe-select__list ${toggleCurriculumState === false ? 'hide' : 'show'}`}>
                            <li className='moe-select__item'>
                                <button onClick={() => changeSwitchCurriculum('nzc')}>NZC</button>
                            </li>
                            <li className='moe-select__item'>
                                <button onClick={() => changeSwitchCurriculum('kwr')}>Kauwhata Reo</button>
                            </li>
                        </ul>
                    </div> */}
                </Node>
            </Collapse>
        );
    }

    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    <div className='moe-header__desktop moe-header__desktop--twk'>
                        <div className='moe-header__desktop-container'>
                            <div className='moe-header__desktop-container-left'>
                                <Logo
                                    {...{
                                        link: logoLink,
                                        image: logoImage,
                                        className: 'ms-header__logo moe-header__logo',
                                        gridSettings: props.context.request.gridSettings,
                                        requestContext: props.context.request,
                                        typeName: props.typeName
                                    }}
                                />
                                <div className='moe-header__logo-txt-column'>
                                    <a href={headerTextLink?.linkUrl.destinationUrl} className='moe-header__logo-txt'>
                                        {headerText}
                                    </a>

                                    {/* <div
                                        className='moe-form__control moe-form__control--switchcurriculum'
                                        aria-label='Switch curriculum form'
                                    >
                                        <div className='moe-select'>
                                            <button onClick={toggleCurriculum} className='moe-select__button'>
                                                {switchCurriculumName}
                                                <span className='moe-select__chevrondown'>
                                                    <BsChevronDown />
                                                </span>
                                            </button>
                                            <a
                                                className='moe-form__control-gobtn'
                                                href={switchCurriculumUrl}
                                                style={{ pointerEvents: switchCurriculumUrl === 'select' ? 'none' : 'auto' }}
                                            >
                                                <span className='sr-only'>Go</span>
                                                <BsFillArrowRightCircleFill />
                                            </a>
                                        </div>
                                        <ul className={`moe-select__list ${toggleCurriculumState === false ? 'hide' : 'show'}`}>
                                            <li className='moe-select__item'>
                                                <button onClick={() => changeSwitchCurriculum('nzc')}>NZC</button>
                                            </li>
                                            <li className='moe-select__item'>
                                                <button onClick={() => changeSwitchCurriculum('kwr')}>Kauwhata Reo</button>
                                            </li>
                                        </ul>
                                    </div> */}
                                </div>
                            </div>
                            <div className='moe-header__desktop-container-right'>
                                <div className='moe-search-wrap'>{_renderReactFragment(props.search)}</div>
                                <div className='moe-header-actions'>
                                    {_renderAccountBlock(props, false, preferredLanguage)}
                                    {props.cartIcon}
                                    <a className='moe-btn moe-btn--primary-blue' role='button' href={supportBtnUrl}>
                                        <text>
                                            {preferredLanguage === 'maori' ? resources.supportBtnTextInMaori : resources.supportBtnText}
                                        </text>
                                    </a>
                                </div>
                            </div>
                            {props.preferredStore}
                            {_renderReactFragment(props.siteOptions)}
                        </div>
                    </div>
                    <div className='moe-header__mobile moe-header__mobile--twk'>
                        <div className='moe-header__mobile-container'>
                            <div className='moe-header__mobile-container-left'>
                                <Logo
                                    {...{
                                        link: logoLink,
                                        image: logoImage,
                                        className: 'ms-header__logo moe-header__logo',
                                        gridSettings: props.context.request.gridSettings,
                                        requestContext: props.context.request,
                                        typeName: props.typeName
                                    }}
                                />
                                <a href={headerTextLink?.linkUrl.destinationUrl} className='moe-header__logo-txt'>
                                    {headerText}
                                </a>
                            </div>
                            <div className='moe-header__mobile-container-right'>
                                <div className='moe-header-actions'>
                                    {_renderAccountBlock(props, false, preferredLanguage)}
                                    {props.cartIcon}
                                    {props.navIcon}
                                </div>
                            </div>
                        </div>
                    </div>
                </Node>
                {_renderCollapseMenu(props)}
            </Node>
            {/* <div className='twk-header-pattern-wrap'></div> */}
            {props.slots.headerPattern && props.slots.headerPattern}
            <div className='header-patternimage-wrap'>{_renderPatternImage(headerPatternImage)}</div>
            <Node className='moe-nav-wrap twk-nav-wrap'>
                {_renderReactFragment(props.menuBar)}
                <div className='moe-switchsite'>
                    <a className='moe-switchsite__link' href={nzcRedirectURL} target='_blank'>
                        <img
                            className='moe-switchsite__img nzc-logo'
                            title='New Zealand Curriculum Online'
                            src={getAsset('images/nzc-thumbnail.png', props.context.request)}
                            alt='New Zealand Curriculum logo'
                        />
                    </a>
                    <a className='moe-switchsite__link' href={kauwhatareoRedirectURL} target='_blank'>
                        <img
                            className='moe-switchsite__img kwr-logo'
                            title='Kauwhata Reo'
                            src={getAsset('images/kwr-thumbnail.png', props.context.request)}
                            alt='Kauwhata Reo logo'
                        />
                    </a>
                </div>
            </Node>
        </Module>
    );
};

const renderCustomerName = (accountInformation: AsyncResult<Customer>): React.ReactChild => {
    const customer = accountInformation && accountInformation.result;
    return <>{customer ? customer.FirstName || customer.Name : ''}</>;
};

function _renderMobileAccountBlock(
    props: IHeaderViewProps & IHeaderProps<{}>,
    renderForMobile: boolean,
    preferredLanguage: string
): JSX.Element | null {
    const { AccountInfoDropdownParentContainer, signInLink, data, accountLinks } = props;
    const signInLinkInMaori = _renderSigninLinkInMaori(props);
    const signOutLinkInMaori = _renderSignoutLinkInMaori(props);
    const signOutLinkInEnglish = _renderSignoutLinkInEnglish(props);
    const customer = props.data.accountInformation && props.data.accountInformation.result;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(
            'ms-header__drawer',
            AccountInfoDropdownParentContainer.className,
            renderForMobile ? 'account-mobile' : 'account-desktop'
        );

        if (accountLinks) {
            _updateUserDetails(props);
            const accountLinksInEnglish = accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false;
            const myaccountLinksInMaori = _getAccountLinksInMaori(props, customer);
            let accountLinksInMaori = myaccountLinksInMaori ? myaccountLinksInMaori.map((link: React.ReactNode) => link) : false;
            if (!accountLinksInMaori) {
                accountLinksInMaori = accountLinksInEnglish;
            }

            return (
                <Drawer
                    className={accountClassName}
                    openGlyph='ms-header__drawer-open'
                    closeGlyph='ms-header__drawer-close'
                    glyphPlacement='end'
                    toggleButtonText={renderCustomerName(data.accountInformation)}
                >
                    <div>
                        {preferredLanguage === 'maori' ? accountLinksInMaori : accountLinksInEnglish}
                        {preferredLanguage === 'maori' ? signOutLinkInMaori : signOutLinkInEnglish}
                    </div>
                </Drawer>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {preferredLanguage === 'maori' ? signInLinkInMaori : signInLink}
                </Node>
            );
        }
    }
    return null;
}

function _renderAccountBlock(
    props: IHeaderViewProps & IHeaderProps<{}>,
    renderForMobile: boolean,
    preferredLanguage: string
): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signInLink,
        accountLinks
    } = props;
    const signInLinkInMaori = _renderSigninLinkInMaori(props);
    const signOutLinkInMaori = _renderSignoutLinkInMaori(props);
    const signOutLinkInEnglish = _renderSignoutLinkInEnglish(props);
    const customer = props.data.accountInformation && props.data.accountInformation.result;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(
            AccountInfoDropdownParentContainer.className,
            renderForMobile ? 'account-mobile' : 'account-desktop'
        );
        if (AccountInfoDropdownPopoverConentContainer) {
            _updateUserDetails(props);
            const accountLinksInEnglish = accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false;
            const myaccountLinksInMaori = _getAccountLinksInMaori(props, customer);
            let accountLinksInMaori = myaccountLinksInMaori ? myaccountLinksInMaori.map((link: React.ReactNode) => link) : false;
            if (!accountLinksInMaori) {
                accountLinksInMaori = accountLinksInEnglish;
            }

            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {preferredLanguage === 'maori' ? accountLinksInMaori : accountLinksInEnglish}
                        {preferredLanguage === 'maori' ? signOutLinkInMaori : signOutLinkInEnglish}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {preferredLanguage === 'maori' ? signInLinkInMaori : signInLink}
                </Node>
            );
        }
    }

    return null;
}

function _updateUserDetails(props: any) {
    const { context } = props;
    const [isUpdatedUserDetails, setIsUpdatedUserDetails] = useState<boolean>(false);

    if (context.request.user?.isAuthenticated) {
        if (!isUpdatedUserDetails) {
            try {
                readAsync({ callerContext: context.actionContext }, context.request.user.customerAccountNumber).then(customerData => {
                    if (
                        customerData.FirstName === context.request.user.firstName &&
                        customerData.LastName === context.request.user.lastName &&
                        customerData.Email === context.request.user.emailAddress
                    ) {
                        setIsUpdatedUserDetails(true);
                        return;
                    }
                    customerData.FirstName = context.request.user.firstName;
                    customerData.LastName = context.request.user.lastName;
                    customerData.Name = context.request.user.name;
                    customerData.Email = context.request.user.emailAddress;

                    const entity = { ...customerData, Attributes: customerData.Attributes };
                    updateAsync({ callerContext: context.actionContext, bypassCache: 'get' }, entity).then(updatedCustomerData => {
                        if (updatedCustomerData) {
                            setIsUpdatedUserDetails(true);
                        }
                    });
                });
            } catch (err) {
                console.error('Get all the customers data err', err);
            }
        }
    }
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

function _renderSigninLinkInMaori(props: IHeaderViewProps & IHeaderProps<{}>): React.ReactNode | undefined {
    const {
        data: { accountInformation },
        context: {
            request: { user }
        },
        resources
    } = props;
    const customer = accountInformation && accountInformation.result;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);

    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignIn);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);

    return (
        !customer && (
            <Button
                className='ms-header__signin-button'
                title={resources.signInTextInMaori}
                href={user && user.signInUrl}
                aria-label={resources.signInTextInMaori}
                onClick={onTelemetryClick(telemetryContent, payLoad, 'Sign in')}
                {...attributes}
            >
                <span className='ms-header__signin-button-text' aria-hidden>
                    {resources.signInTextInMaori}
                </span>
            </Button>
        )
    );
}

function _renderSignoutLinkInEnglish(props: IHeaderViewProps & IHeaderProps<{}>): React.ReactNode | undefined {
    const {
        data: { accountInformation },
        context: {
            request: { user }
        },
        resources
    } = props;
    const customer = accountInformation && accountInformation.result;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const displayName = customer ? customer.FirstName || customer.Name : undefined;

    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignOut);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);

    const deleteAllCookies = () => {
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }
        onTelemetryClick(telemetryContent, payLoad, 'Sign out');
        //location.href = user.signOutUrl as any;
    };

    return (
        customer &&
        displayName && (
            <Button
                className='ms-header__signout-button'
                title={resources.signOutLinkText}
                href={(user && user.signOutUrl) || ''}
                aria-label={resources.signOutLinkText}
                onClick={deleteAllCookies}
                {...attributes}
            >
                <span className='ms-header__signout-button-text' aria-hidden>
                    {resources.signOutLinkText}
                </span>
            </Button>
        )
    );
}

function _renderSignoutLinkInMaori(props: IHeaderViewProps & IHeaderProps<{}>): React.ReactNode | undefined {
    const {
        data: { accountInformation },
        context: {
            request: { user }
        },
        resources
    } = props;
    const customer = accountInformation && accountInformation.result;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const displayName = customer ? customer.FirstName || customer.Name : undefined;

    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignOut);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);

    const deleteAllCookies = () => {
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }

        onTelemetryClick(telemetryContent, payLoad, 'Sign out');
        //location.href = user.signOutUrl as any;
    };
    return (
        customer &&
        displayName && (
            <Button
                className='ms-header__signout-button'
                title={resources.signOutTextInMaori}
                href={(user && user.signOutUrl) || ''}
                aria-label={resources.signOutTextInMaori}
                onClick={deleteAllCookies}
                {...attributes}
            >
                <span className='ms-header__signout-button-text' aria-hidden>
                    {resources.signOutTextInMaori}
                </span>
            </Button>
        )
    );
}

function _getAccountLinksInMaori(props: IHeaderViewProps & IHeaderProps<{}>, customer: any) {
    const { myAccountLinksInMaori } = props.config;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const payLoad = getPayloadObject('click', telemetryContent, '');
    const displayName = customer ? customer.FirstName || customer.Name : undefined;
    if (!customer || !displayName || !myAccountLinksInMaori || myAccountLinksInMaori.length === 0) {
        return undefined;
    }

    const handleLinkTextChange = (linkIndex: number): any => (event: Msdyn365.ContentEditableEvent) => {
        if (props.config.myAccountLinks && props.config.myAccountLinks[linkIndex]) {
            props.config.myAccountLinks[linkIndex].linkText = event.target.value;
        }
    };

    return myAccountLinksInMaori.map((cta: IMyAccountLinksInMaoriData, index: number) => {
        payLoad.contentAction.etext = cta.linkText;
        const attributes = getTelemetryAttributes(telemetryContent, payLoad);
        const editableLink: Msdyn365.ILinksData = {
            ariaLabel: cta.ariaLabel,
            className: 'ms-signin-info__account-link-button',
            linkText: cta.linkText,
            linkUrl: cta.linkUrl.destinationUrl,
            openInNewTab: cta.openInNewTab,
            role: 'button',
            additionalProperties: attributes
        };

        return (
            <Msdyn365.Link
                key={index}
                link={editableLink}
                editProps={{ onTextChange: handleLinkTextChange(index), requestContext: props.context.request }}
            />
        );
    });
}

export default headerView;
